import type { FieldError, FieldErrors, FieldValues } from "react-hook-form";

type ScrollToFirstErrorOptions = {
  /**
   * Top-level error keys routed to a custom DOM anchor instead of looking up by `[name=path]`.
   * Use for fields whose error renders outside the standard FormField (e.g., footerCustom on a
   * dropzone) or fields not registered with RHF (no `name` attribute exists on the DOM).
   */
  customAnchors?: Record<string, HTMLElement | null | undefined>;
  /**
   * Scope the `[name=path]` lookup to a specific element (e.g., the form root). Defaults to document.
   */
  root?: Document | HTMLElement;
};

/**
 * Node in an RHF errors tree: a leaf `FieldError`, a nested object of errors, or an array
 * (for field arrays — items can be sparse / null when only some indices have errors).
 */
type ErrorBranch = { [key: string]: ErrorNode | undefined };
type ErrorArray = Array<ErrorNode | null | undefined>;
type ErrorNode = FieldError | ErrorBranch | ErrorArray;

const isLeafError = (node: ErrorBranch | FieldError): node is FieldError => "type" in node && "message" in node;

/**
 * Walk an error subtree and return the first leaf path (matching the `{type, message}` shape).
 * Iteration order is insertion order which mirrors yup/RHF field declaration order.
 */
const findFirstLeafPath = (node: ErrorNode | null | undefined, prefix: string): string | null => {
  if (!node) return null;
  if (Array.isArray(node)) {
    for (let i = 0; i < node.length; i++) {
      const path = findFirstLeafPath(node[i], `${prefix}.${i}`);
      if (path) return path;
    }
    return null;
  }
  if (isLeafError(node)) return prefix;
  for (const k of Object.keys(node)) {
    const path = findFirstLeafPath(node[k], `${prefix}.${k}`);
    if (path) return path;
  }
  return null;
};

/**
 * Resolve a leaf error path to a scroll-target element via `[name="path"]`, then walk up to its
 * `.MuiFormControl-root` wrapper so the scroll lands on the labelled field rather than the bare input.
 */
const resolveFieldElement = (path: string, root: Document | HTMLElement): HTMLElement | null => {
  const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(path) : path.replace(/"/g, '\\"');
  const el = root.querySelector(`[name="${escaped}"]`);
  if (!(el instanceof HTMLElement)) return null;
  const wrapper = el.closest(".MuiFormControl-root");
  return wrapper instanceof HTMLElement ? wrapper : el;
};

/**
 * Collect candidate scroll-targets from RHF errors.
 *  - Custom keys → provided anchor
 *  - Other keys → DOM element looked up by `name` attribute (matches Form.tsx convention)
 */
const collectCandidates = (
  errors: FieldErrors<FieldValues>,
  customAnchors: ScrollToFirstErrorOptions["customAnchors"],
  root: Document | HTMLElement,
): HTMLElement[] => {
  const out: HTMLElement[] = [];
  // FieldErrors<FieldValues> resolves to `Partial<any>` due to RHF generics; coerce once to our
  // strict ErrorBranch shape so the walker stays fully typed downstream.
  const tree = errors as ErrorBranch;
  for (const key of Object.keys(tree)) {
    if (customAnchors && key in customAnchors) {
      const anchor = customAnchors[key];
      if (anchor) out.push(anchor);
      continue;
    }
    const path = findFirstLeafPath(tree[key], key);
    if (!path) continue;
    const el = resolveFieldElement(path, root);
    if (el) out.push(el);
  }
  return out;
};

/**
 * Pick element appearing earliest in document order using compareDocumentPosition (does not rely
 * on layout, so works for off-screen elements too).
 */
const pickTopmost = (els: HTMLElement[]): HTMLElement | null => {
  if (els.length === 0) return null;
  return els.reduce((first, cur) =>
    first === cur || !(first.compareDocumentPosition(cur) & Node.DOCUMENT_POSITION_PRECEDING) ? first : cur,
  );
};

/**
 * Scroll to the topmost errored field in the form.
 * Pass `customAnchors` for fields whose error doesn't have a usable `[name="path"]` element
 * (e.g., the certificate-files area whose error is rendered via a custom footer).
 */
export const scrollToFirstError = (errors: FieldErrors<FieldValues>, options: ScrollToFirstErrorOptions = {}): void => {
  const root = options.root ?? document;
  const target = pickTopmost(collectCandidates(errors, options.customAnchors, root));
  target?.scrollIntoView({ behavior: "smooth", block: "center" });
};
